import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UtilitiesService } from '@nx-ahs/shared/services';
import {
	PageHeaderComponent,
	AppSpinnerComponent,
	ProgressButtonComponent,
	DbIconComponent
} from '@nx-ahs/shared/components';
import moment from 'moment';
import { MailService } from '../services/mail.service';
import { TimeoutService } from '@nx-ahs/timeout';

@UntilDestroy()
@Component({
	selector: 'nx-ahs-return-mail-batch',
	templateUrl: './return-mail-batch.component.html',
	styleUrls: ['./return-mail-batch.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatTableModule,
		MatButtonModule,
		MatCardModule,
		FontAwesomeModule,
		PageHeaderComponent,
		AppSpinnerComponent,
		ProgressButtonComponent,
		DbIconComponent
	]
})
export class ReturnMailBatchComponent implements OnInit {
	@ViewChild(MatTable) table: MatTable<any>;

	//#region Public Members
	public displayedColumns: string[] = [
		'identificationBarcode',
		'returnedDate',
		'postmarkedDate',
		'returnMailReasonId'
	];
	public loading: boolean = false;
	public maxReturnedDate: Date = new Date();
	public maxPostmarkedDate: Date = new Date();
	public returnMailBatchForm: FormGroup;
	public returnMailReasons: any[] = [];
	public returnMailList: any[] = [];
	public saving: boolean = false;
	public validationResults: any;
	//#endregion Public Members

	//#region Constructor
	constructor(
		private formBuilder: FormBuilder,
		private mailService: MailService,
		private utilityService: UtilitiesService,
		private snackBar: MatSnackBar,
		private timeoutService: TimeoutService
	) {}
	//#endregion Constructor

	//#region Lifecycle Events
	ngOnInit(): void {
		this.getRefData();
		this.buildForms();
	}
	//#endregion Lifecycle Events

	//#region Public Methods
	public getReturnMailReasonFromId(returnMailReasonId: number): any {
		const reason = this.returnMailReasons.filter((r) => r.returnMailReasonId === returnMailReasonId)[0];
		return reason;
	}

	public async processReturns(): Promise<any> {
		this.saving = true;
		const processableReturns = this.returnMailList.filter((r) => {
			return !r.isReturned && r.letterId !== null;
		});

		if (processableReturns.length > 0) {
			const letterIds = processableReturns.map((r) => {
				return r.letterId;
			});

			await this.mailService.returnMail_ProcessReturns(
				letterIds,
				processableReturns[0].returnedDate,
				processableReturns[0].postmarkedDate,
				processableReturns[0].returnMailReasonId
			);
			this.snackBar.open('Returns processed successfully', 'DISMISS', { duration: 5000 });
			this.reset();
		}
		this.saving = false;
	}

	public validate(): void {
		this.utilityService.markAllFieldsUntouched(this.returnMailBatchForm);
		const identificationBarcodes = this.returnMailList.map((r) => {
			return r.identificationBarcode;
		});
		this.mailService.returnMail_ValidateBarcodes(identificationBarcodes).then((results) => {
			results.forEach((result) => {
				const returnMail = this.returnMailList.find(
					(r) => r.identificationBarcode === result.identificationBarcode
				);
				returnMail.letterId = result.letterId;
				returnMail.isReturned = result.isReturned;
			});
			if (!this.displayedColumns.includes('letterId')) {
				this.displayedColumns.unshift('letterId');
			}
			if (!this.displayedColumns.includes('returnedStatus')) {
				this.displayedColumns.push('returnedStatus');
			}
			this.table.renderRows();

			const validBarcodeCount = results.filter((r) => {
				return r.letterId !== null && !r.isReturned;
			}).length;
			const invalidBarcodeCount = results.filter((r) => {
				return r.letterId === null;
			}).length;
			const returnedMailCount = results.filter((r) => {
				return r.isReturned;
			}).length;
			this.validationResults = {
				validBarcodeCount: validBarcodeCount,
				invalidBarcodeCount: invalidBarcodeCount,
				returnedMailCount: returnedMailCount
			};
		});
	}
	//#endregion Public Methods

	//#region Private Methods
	private buildForms(): void {
		this.returnMailBatchForm = this.formBuilder.group({
			identificationBarcode: [null, Validators.required],
			returnedDate: [new Date(), Validators.required],
			postmarkedDate: [new Date(), Validators.required],
			returnMailReasonId: [null, Validators.required]
		});

		//Subscribe to the valueChanges event so we can monitor the IdentificationBarcode textbox
		//When the length of that text box is 16 then we check if its a duplicate
		//if not a duplicate, push a new record into returnMailList
		//if a duplicate, update the record (in case the user changed the Reason or Date) and inform the user
		this.returnMailBatchForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			this.utilityService.markAllFieldsTouched(this.returnMailBatchForm);

			if (this.returnMailBatchForm.valid && value.identificationBarcode.trim().length === 16) {
				const index = this.returnMailList.findIndex(
					(r) => r.identificationBarcode == value.identificationBarcode
				);
				if (index == -1) {
					this.returnMailList.push({
						letterId: undefined,
						identificationBarcode: value.identificationBarcode,
						returnedDate: value.returnedDate,
						postmarkedDate: value.postmarkedDate,
						returnMailReasonId: value.returnMailReasonId,
						returnedStatus: undefined
					});
				} else {
					this.returnMailList[index].returnedDate = value.returnedDate;
					this.returnMailList[index].returnMailReasonId = value.returnMailReasonId;
					this.snackBar.open(
						'Barcode (' + value.identificationBarcode + ') has already been added to this list.',
						'DISMISS',
						{ duration: 5000 }
					);
				}
				this.returnMailBatchForm.get('identificationBarcode').setValue('');
				this.table.renderRows();

				//update action date, otherwise users who continuously add new letters with the same settings can eventually be warned of timeout
				this.timeoutService.actionTaken();
			}
		});
	}

	private getRefData(): Promise<any> {
		return this.mailService
			.getReturnMailReasons()
			.toPromise()
			.then((results) => {
				this.returnMailReasons = results;
			});
	}

	private reset(): void {
		this.validationResults = null;
		this.returnMailList = [];
		this.displayedColumns = ['identificationBarcode', 'returnedDate', 'postmarkedDate', 'returnMailReasonId'];
		this.buildForms();
	}
	//#endregion Private Methods
}
